import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Organization } from "@models/Organization";
import BareScreen from "@components/General/BareScreen";
import styles from "@styles/screens/HourBankDetailScreen.module.css";
import { BarChart, ChartsText, ChartsTooltipContainer, useItemTooltip } from "@mui/x-charts";
import Loading from "@components/General/Loading";
import { GridTableCell, GridTableRow, PaginatedGridTable, TableColumn } from "@shared/designSystem/Tables";
import { HorizontalContainer, SimpleCardContainer, VerticalContainer } from "@shared/designSystem/Containers";
import { NormalButton } from "@components/General/Button";
import { BoldText, Text } from "@shared/designSystem/Texts";
import { usePopup } from "@contexts/popupContext";
import { Modal } from "@shared/designSystem/messages/Modal";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { HollowedButton } from "@shared/designSystem/Button";
import { MultiDatePickerPopper } from "@components/General/TablePoppers";
import { TextInput } from "@components/General/TextInput";
import ResultPopup from "@components/General/ResultPopup";
import { Application } from "@core/Application";
import CircularCheckbox from "@components/General/CircularCheckbox";
import { CalendarMonthOutlined, EditNoteOutlined, LocalAtmOutlined, RemoveCircleOutlineOutlined } from "@mui/icons-material";
import { HourBankEntry } from "@services/HourBankService";
import { Loader } from "@shared/designSystem/Loader";

type BalanceEntry = {
    id: string;
    presenteeismRecordID: string;
    date: string;
    shiftRecipeID?: string;
    motive?: string;
    workedMinutes: number;
    expectedMinutes: number;
    minutesDifference: number;
    entryType: "REGULAR" | "CREDIT" | "MANUAL" | "DEBIT-USE";
};

interface AddEntryPopupProps {
    timezone: string;
    onSubmit: (date: string, workedMinutes: number, motive: string) => Promise<unknown>;
}

const isCreditEntry = (entry: BalanceEntry) => entry.entryType === "CREDIT";
const isManualEntry = (entry: BalanceEntry) => entry.entryType === "MANUAL";
const isDebitUseEntry = (entry: BalanceEntry) => entry.entryType === "DEBIT-USE";
const isRegularEntry = (entry: BalanceEntry) => entry.entryType === "REGULAR";
const hidesWorkedAndExpected = (entry: BalanceEntry) =>
    isManualEntry(entry) || isCreditEntry(entry) || isDebitUseEntry(entry);

const CustomAxisTickLabel = (props: any) => {
    const combinedStyle = {
        ...props.style,
        fill: "#9F9F9F",
        textAlign: "center",
        fontFamily: "Inter, sans-serif",
        fontSize: "12px",
        fontStyle: "normal",
        fontWeight: 600,
        lineHeight: "normal",
    };

    return <ChartsText {...props} style={combinedStyle} text={props.text} />;
};

const toHoursValue = (minutes: number) => Number((minutes / 60).toFixed(2));

const BalanceBarTooltip = ({ entries }: { entries: BalanceEntry[] }) => {
    const tooltipData = useItemTooltip<"bar">();
    const dataIndex = tooltipData?.identifier && "dataIndex" in tooltipData.identifier
        ? tooltipData.identifier.dataIndex
        : undefined;

    if (dataIndex == null) {
        return null;
    }

    const entry = entries[dataIndex];
    if (!entry) {
        return null;
    }

    const expectedValue = toHoursValue(Math.min(entry.workedMinutes, entry.expectedMinutes));
    const differenceValue = toHoursValue(Math.abs(entry.workedMinutes - entry.expectedMinutes));
    const differenceColor = entry.workedMinutes >= entry.expectedMinutes ? "#56a725" : "#ff5454";

    return (
        <ChartsTooltipContainer trigger="item" anchor="pointer" position="top">
            <div style={{ background: "#fff", border: "1px solid #e6e6e6", borderRadius: "8px", padding: "8px 10px", fontSize: "12px", minWidth: "150px" }}>
                <div style={{ color: "#6f6f78", marginBottom: "6px", fontWeight: 600 }}>{entry.date}</div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "4px" }}>
                    <span style={{ color: "#6f6f78" }}>Esperadas</span>
                    <span style={{ color: "#1e1d22", fontWeight: 600 }}>{expectedValue}h</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
                    <span style={{ color: differenceColor }}>Diferencia</span>
                    <span style={{ color: differenceColor, fontWeight: 600 }}>{differenceValue}h</span>
                </div>
            </div>
        </ChartsTooltipContainer>
    );
};

const BalanceHeader = ({
    entries,
    loading,
}: {
    entries: BalanceEntry[];
    loading: boolean;
}) => {

    const [open, setOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [selectedDates, setSelectedDates] = useState<Date[]>([])

    const toISODateKey = (value: Date): string => {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const handleAcceptDateInput = (dates: Date[]) => {
        setSelectedDates(dates)
        setOpen(false)
    }

    const filteredEntries = useMemo(() => {
        if (!entries.length) {
            return [];
        }

        const selectedDateKeys = new Set(selectedDates.map((selectedDate) => toISODateKey(selectedDate)));
        const filteredByDate = !selectedDates.length
            ? entries
            : entries.filter((entry) => selectedDateKeys.has(entry.date));

        return filteredByDate
    }, [entries, selectedDates]);

    const regularEntries = filteredEntries.filter(isRegularEntry);
    const latestEntries = regularEntries.slice(-365);

    const totals = latestEntries.reduce((acc, entry) => {
        acc.workedMinutes += entry.workedMinutes;
        acc.expectedMinutes += entry.expectedMinutes;
        return acc;
    }, { workedMinutes: 0, expectedMinutes: 0 });

    const creditMinutes = filteredEntries.reduce((acc, entry) => {
        if (!isCreditEntry(entry)) {
            return acc;
        }

        return acc + entry.minutesDifference;
    }, 0);

    const manualMinutes = filteredEntries.reduce((acc, entry) => {
        if (!isManualEntry(entry)) {
            return acc;
        }

        return acc + entry.minutesDifference;
    }, 0);

    const balanceMinutes = filteredEntries.reduce((acc, entry) => {
        if (isCreditEntry(entry)) {
            return acc;
        }

        return acc + entry.minutesDifference;
    }, 0);
    const balanceClass =
        balanceMinutes > 0
            ? styles["balance-positive"]
            : balanceMinutes < 0
                ? styles["balance-negative"]
                : styles["balance-neutral"];

    return (
        <SimpleCardContainer className={styles["balance-header"]}>
            <div className={styles["balance-header-top"]}>
                <HorizontalContainer justifyContent="space-between" paddingLeft={"10px"} margin={"0px 0px 10px 0px"}>
                    <BoldText text={"Grafico de horas"} size="sm" />
                    <div style={{ height: "50px", width: "150px" }}>
                        <NormalButton text="Elegir fecha/rango" onClick={(event) => {
                            setAnchorEl(event.currentTarget)
                            setOpen(!open)
                        }}>
                            <CalendarMonthOutlined />
                        </NormalButton>
                        {open && <MultiDatePickerPopper 
                            isOpen={open}
                            anchorElement={anchorEl}
                            placement="bottom-end"
                            clickAwayHandler={() => setOpen(false)}
                            selectedDates={selectedDates}
                            setSelectedDates={handleAcceptDateInput}
                            zIndex={1001}
                            range={true}
                        />
                        }
                    </div>
                </HorizontalContainer>
            </div>

            {loading && (
                <div className={styles["balance-loading"]}>
                    <Loader />
                </div>
            )}
            
            {!loading && (
            <div className={styles["balance-header-content"]}>
                <div className={styles["balance-chart"]}>  
                    <BarChart
                        xAxis={[
                            {
                                scaleType: "band",
                                data: latestEntries.map((entry) => entry.date),
                                tickLabelInterval: "auto",
                                tickLabelMinGap: 14,
                            }
                        ]}
                        series={[
                            {
                                data: latestEntries.map((entry) =>
                                    toHoursValue(Math.min(entry.workedMinutes, entry.expectedMinutes))
                                ),
                                label: "Esperadas",
                                color: "#c7c7c7",
                                stack: "total",
                                valueFormatter: (value) => `${value}h`,
                            },
                            {
                                data: latestEntries.map((entry) =>
                                    toHoursValue(Math.abs(entry.workedMinutes - entry.expectedMinutes))
                                ),
                                label: "Diferencia",
                                color: "#56a725",
                                stack: "total",
                                colorGetter: ({ dataIndex }) => {
                                    const entry = latestEntries[dataIndex];
                                    if (!entry) {
                                        return "#56a725";
                                    }
                                    return entry.workedMinutes >= entry.expectedMinutes ? "#56a725" : "#ff5454";
                                },
                                valueFormatter: (value) => `${value}h`,
                            },
                        ]}
                        yAxis={[{ valueFormatter: (value) => `${value}h` }]}
                        axisHighlight={{ x: "none", y: "none" }}
                        height={240}
                        margin={{ top: 20, right: 20, bottom: 30, left: 40 }}
                        hideLegend
                        slots={{
                            axisTickLabel: CustomAxisTickLabel,
                            tooltip: () => <BalanceBarTooltip entries={latestEntries} />,
                        }}
                        sx={{
                            width: "100%",
                            height: "100%",
                            "& .MuiChartsAxisHighlight-root": {
                                stroke: "rgba(106, 199, 49, 0.25)",
                                strokeDasharray: "none",
                            },
                            "& .MuiChartsAxis-line": {
                                stroke: "transparent",
                                strokeDasharray: "none",
                            },
                            "& .MuiChartsAxis-tick": {
                                stroke: "#F2F2F2",
                                strokeDasharray: "none",
                            },
                        }}
                        slotProps={{
                            axisLine: { style: { stroke: "transparent", strokeDasharray: "none" } },
                            axisTick: { style: { stroke: "#F2F2F2", strokeDasharray: "none" } },
                            tooltip: {
                                trigger: "item",
                                anchor: "pointer",
                                position: "top",
                            },
                        }}
                    />
                </div>
                <div className={styles["balance-total"]}>
                    <div className={styles["balance-total-label"]}>Balance total</div>
                    <div className={`${styles["balance-total-value"]} ${balanceClass}`}>
                        {balanceMinutes > 0 ? "+" : balanceMinutes < 0 ? "-" : ""}
                        {minutesToHoursMinutes(Math.abs(balanceMinutes))}
                    </div>
                    <div className={styles["balance-total-sub"]}>
                        {minutesToHoursMinutes(totals.workedMinutes)} trabajadas
                    </div>
                    {manualMinutes !== 0 && (
                        <div className={styles["credit-summary"]}>
                            <div className={styles["credit-summary-label"]}>Ajustes manuales</div>
                            <div className={`${styles["credit-summary-value"]} ${manualMinutes > 0 ? styles["difference-positive"] : styles["difference-negative"]}`}>
                                {manualMinutes > 0 ? "+" : "-"}
                                {minutesToHoursMinutes(Math.abs(manualMinutes))}
                            </div>
                        </div>
                    )}
                    {creditMinutes !== 0 && (
                        <div className={styles["credit-summary"]}>
                            <div className={styles["credit-summary-label"]}>Crédito disponible</div>
                            <div className={`${styles["credit-summary-value"]} ${styles["credit-value"]}`}>
                                +{minutesToHoursMinutes(Math.abs(creditMinutes))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            )}
        </SimpleCardContainer>
    );
};

const EntriesTable = ({
    entries,
    onAddEntryClick,
    loading
}: {
    entries: BalanceEntry[];
    onAddEntryClick: () => void;
    loading: boolean;
}) => {
    const [open, setOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [selectedDates, setSelectedDates] = useState<Date[]>([])

    const toISODateKey = (value: Date): string => {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const tableColumns: TableColumn[] = useMemo(() => ([
        { id: "date", label: "Fecha", width: "1fr" },
        { id: "motive", label: "Motivo", width: "1fr" },
        { id: "worked", label: "Trabajadas", width: "1fr", align: "center" },
        { id: "expected", label: "Esperadas", width: "1fr", align: "center" },
        { id: "difference", label: "Diferencia", width: "1fr", align: "center" }
    ]), []);

    const handleAcceptDateInput = (dates: Date[]) => {
        setSelectedDates(dates)
        setOpen(false)
    }

    const filteredEntries = useMemo(() => {
        if (!entries.length) {
            return [];
        }

        const selectedDateKeys = new Set(selectedDates.map((selectedDate) => toISODateKey(selectedDate)));
        const filteredByDate = !selectedDates.length
            ? entries
            : entries.filter((entry) => selectedDateKeys.has(entry.date));

        return [...filteredByDate].sort((a, b) => b.date.localeCompare(a.date));
    }, [entries, selectedDates]);

    return (
        <SimpleCardContainer width={"100%"} height={"auto"} margin={"0 0 24px 0"}>
            <HorizontalContainer justifyContent="space-between" paddingLeft={"10px"} margin={"0px 0px 10px 0px"}>
                <BoldText text={"Registro de horas"} size="sm" />
                <HorizontalContainer gap={"8px"} width={"auto"}>
                    <div style={{ height: "50px", width: "150px" }}>
                        <NormalButton text="Elegir fecha/rango" onClick={(event) => {
                            setAnchorEl(event.currentTarget)
                            setOpen(!open)
                        }}>
                            <CalendarMonthOutlined />
                        </NormalButton>
                        {open && <MultiDatePickerPopper 
                            isOpen={open}
                            anchorElement={anchorEl}
                            placement="bottom-end"
                            clickAwayHandler={() => setOpen(false)}
                            selectedDates={selectedDates}
                            setSelectedDates={handleAcceptDateInput}
                            zIndex={1001}
                            range={true}
                        />
                        }
                    </div>
                    <div style={{ height: "50px", width: "150px" }}>
                        <NormalButton
                            text="Agregar registro"
                            onClick={onAddEntryClick}
                        />
                    </div>
                </HorizontalContainer>
            </HorizontalContainer>
            <PaginatedGridTable
                columns={tableColumns}
                items={filteredEntries}
                isLoading={loading}
                renderRow={(entry, index) => {
                    const difference = entry.minutesDifference;
                    const differenceClass =
                        isCreditEntry(entry)
                            ? styles["credit-value"]
                            : difference > 0
                            ? styles["difference-positive"]
                            : difference < 0
                                ? styles["difference-negative"]
                                : styles["difference-neutral"];
                    const differencePrefix =
                        isCreditEntry(entry)
                            ? "+"
                            : difference > 0
                                ? "+"
                                : difference < 0
                                    ? "-"
                                    : "";
                    return (
                        <GridTableRow
                            key={entry.id || index}
                            className={
                                isCreditEntry(entry)
                                    ? styles["credit-row"]
                                    : isDebitUseEntry(entry)
                                        ? styles["debit-use-row"]
                                    : isManualEntry(entry)
                                        ? styles["manual-row"]
                                        : undefined
                            }
                        >
                            <GridTableCell>{entry.date}</GridTableCell>
                            <GridTableCell>
                                <div className={styles["motive-cell"]}>
                                    {isCreditEntry(entry) && (
                                        <span className={styles["credit-badge"]}>
                                            <LocalAtmOutlined fontSize="inherit" />
                                            Crédito
                                        </span>
                                    )}
                                    {isManualEntry(entry) && (
                                        <span className={styles["manual-badge"]}>
                                            <EditNoteOutlined fontSize="inherit" />
                                            Manual
                                        </span>
                                    )}
                                    {isDebitUseEntry(entry) && (
                                        <span className={styles["debit-use-badge"]}>
                                            <RemoveCircleOutlineOutlined fontSize="inherit" />
                                            Uso de debito
                                        </span>
                                    )}
                                    <span>{entry.motive || (isManualEntry(entry) ? "Ajuste manual" : "Uso del sistema")}</span>
                                </div>
                            </GridTableCell>
                            <GridTableCell align="center">
                                {hidesWorkedAndExpected(entry) ? "-" : minutesToHoursMinutes(entry.workedMinutes)}
                            </GridTableCell>
                            <GridTableCell align="center">
                                {hidesWorkedAndExpected(entry) ? "-" : minutesToHoursMinutes(entry.expectedMinutes)}
                            </GridTableCell>
                            <GridTableCell align="center" className={differenceClass}>
                                {differencePrefix}
                                {minutesToHoursMinutes(Math.abs(difference))}
                            </GridTableCell>
                        </GridTableRow>
                    );
                }}
                pageSize={10}
            />
        </SimpleCardContainer>
    );
};

const AddEntryPopup: React.FC<AddEntryPopupProps> = ({ timezone, onSubmit }) => {
    const { hidePopup, showPopup } = usePopup();
    const [workedMinutesInput, setWorkedMinutesInput] = useState("");
    const [workedMinutesError, setWorkedMinutesError] = useState("");
    const [motiveInput, setMotiveInput] = useState("");
    const [motiveError, setMotiveError] = useState("");
    const [dateError, setDateError] = useState("");
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [openDatePicker, setOpenDatePicker] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [loading, setLoading] = useState(false);
    const [entrySign, setEntrySign] = useState<"positive" | "negative">("positive");

    const handleDateClick = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget);
        setOpenDatePicker((previousOpen) => !previousOpen);
    };

    const handleDateSelection = (dates: Date[]) => {
        setSelectedDate(dates.length > 0 ? dates[0] : null);
        setDateError("");
        setOpenDatePicker(false);
    };

    const handleSave = async () => {
        const workedMinutes = Number.parseInt(workedMinutesInput, 10);
        const isMinutesInvalid = Number.isNaN(workedMinutes) || workedMinutes <= 0;
        const trimmedMotive = motiveInput.trim();
        const isMotiveInvalid = trimmedMotive.length === 0;

        if (!selectedDate) {
            setDateError("Selecciona una fecha".localized());
        }
        if (isMinutesInvalid) {
            setWorkedMinutesError("Ingresa minutos válidos".localized());
        }
        if (isMotiveInvalid) {
            setMotiveError("Ingresa un motivo".localized());
        }
        if (!selectedDate || isMinutesInvalid || isMotiveInvalid) {
            return;
        }

        setLoading(true);
        try {
            const date = selectedDate.toLocaleDateString("en-ZA", { timeZone: timezone }).replace(/\//g, "-");
            const signedMinutes = entrySign === "positive" ? workedMinutes : -workedMinutes;
            await onSubmit(date, signedMinutes, trimmedMotive);
            showPopup(() => <ResultPopup result={"Registro agregado correctamente".localized()} succesful={true} />);
        } catch (error) {
            showPopup(() => <ResultPopup result={"Error al agregar el registro".localized()} succesful={false} />);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={true}
            onClose={hidePopup}
            title={"Registro manual".localized()}
            maxWidth="520px"
        >
            <VerticalContainer gap={designSystemStyles.gapSm} alignment="left">
                <div>
                    <Text text={"Fecha".localized()} />
                    <div className={styles["date-input"]} onClick={handleDateClick}>
                        <Text
                            text={selectedDate
                                ? selectedDate.toLocaleDateString("es-AR", { timeZone: timezone })
                                : "Seleccionar fecha".localized()
                            }
                            color={selectedDate ? "#1E1D22" : "#7A7A85"}
                        />
                    </div>
                    {dateError && <Text text={dateError} color="#d64545" />}
                    {openDatePicker && (
                        <MultiDatePickerPopper
                            isOpen={openDatePicker}
                            anchorElement={anchorEl}
                            placement="bottom-start"
                            clickAwayHandler={() => setOpenDatePicker(false)}
                            selectedDates={selectedDate ? [selectedDate] : []}
                            setSelectedDates={handleDateSelection}
                            zIndex={1001}
                        />
                    )}
                </div>

                <TextInput
                    label={"Minutos trabajados".localized()}
                    placeholder={"Ej: 480".localized()}
                    value={workedMinutesInput}
                    onChange={(value) => {
                        setWorkedMinutesInput(value.replace(/\D/g, ""));
                        setWorkedMinutesError("");
                    }}
                    error={workedMinutesError}
                    loading={loading}
                />
                <TextInput
                    label={"Motivo".localized()}
                    placeholder={"Describe el motivo".localized()}
                    value={motiveInput}
                    onChange={(value) => {
                        setMotiveInput(value);
                        setMotiveError("");
                    }}
                    error={motiveError}
                    loading={loading}
                />
                <div className={styles["entry-sign-container"]}>
                    <div className={styles["entry-sign-option"]} onClick={() => setEntrySign("positive")}>
                        <CircularCheckbox value={entrySign === "positive"} onChange={() => setEntrySign("positive")} />
                        <Text text={"Positivo".localized()} />
                    </div>
                    <div className={styles["entry-sign-option"]} onClick={() => setEntrySign("negative")}>
                        <CircularCheckbox value={entrySign === "negative"} onChange={() => setEntrySign("negative")} />
                        <Text text={"Negativo".localized()} />
                    </div>
                </div>

                <HorizontalContainer justifyContent={"space-between"} width="100%" gap={designSystemStyles.gapSm}>
                    <div style={{width: "120px", height: "50px"}}>
                        <HollowedButton onClick={hidePopup} text={"Cancelar".localized()} disabled={loading} />
                    </div>

                    <div style={{width: "120px", height: "50px"}}>
                        <NormalButton onClick={handleSave} text={"Guardar".localized()} loading={loading} />
                    </div>
                </HorizontalContainer>
            </VerticalContainer>
        </Modal>
    );
};

function minutesToHoursMinutes(minutes: number): string {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}h ${mins}m`;
}

const HourBankDetailScreen: React.FC<{ org: Organization }> = ({ org: _org }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state;
    const [entries, setEntries] = useState<BalanceEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const { showPopup } = usePopup();

    const userId = useMemo(() => {
        return new URLSearchParams(location.search).get("id");
    }, [location.search]);

    const mapHourBankEntryToBalanceEntry = (entry: HourBankEntry): BalanceEntry => ({
        id: entry.id,
        presenteeismRecordID: entry.presenteeismRecordID,
        date: new Date(entry.timestamp).toLocaleDateString("en-ZA", { timeZone: _org.timezone }).replace(/\//g, "-"),
        shiftRecipeID: entry.shiftRecipeID,
        workedMinutes: entry.minutesWorked,
        expectedMinutes: entry.minutesExpected,
        motive: entry.motive,
        minutesDifference: entry.minutesDifference,
        entryType: entry.entryType,
    });

    const fetchCollaboratorEntries = async (collaboratorID: string) => {
        setLoading(true);
        try {
            const hourBankService = Application.shared().coreDependencies.hourBankService;
            const serviceEntries = await hourBankService.getHourBankColaboratorEntries(_org.id, collaboratorID);

            const mappedEntries = serviceEntries.map(mapHourBankEntryToBalanceEntry);

            const mergedEntriesMap = new Map<string, BalanceEntry>();
            const nonMergeableEntries: BalanceEntry[] = [];

            mappedEntries.forEach((entry) => {
                if (!isRegularEntry(entry) || !entry.shiftRecipeID) {
                    nonMergeableEntries.push(entry);
                    return;
                }

                const mergeKey = `${entry.date}::${entry.shiftRecipeID}`;
                const existingEntry = mergedEntriesMap.get(mergeKey);

                if (!existingEntry) {
                    mergedEntriesMap.set(mergeKey, { ...entry });
                    return;
                }

                existingEntry.workedMinutes += entry.workedMinutes;
                existingEntry.minutesDifference += entry.minutesDifference;
            });

            const normalizedEntries = [
                ...nonMergeableEntries,
                ...Array.from(mergedEntriesMap.values()),
            ].sort((a, b) => a.date.localeCompare(b.date));

            setEntries(normalizedEntries);
        } catch (error) {
            console.error(error);
        } finally {
            setTimeout(() => setLoading(false), 2500);
        }
    };

    useEffect(() => {
        if (!userId) {
            return;
        }

        fetchCollaboratorEntries(userId);
    }, [_org.id, _org.timezone, userId]);

    const handleSubmitNewEntry = async (date: string, workedMinutes: number, motive: string) => {
        if (!userId) {
            return null;
        }

        const hourBankService = Application.shared().coreDependencies.hourBankService;
        await hourBankService.addHourBankBalanceEntry(_org.id, userId, date, workedMinutes, motive);
        await fetchCollaboratorEntries(userId);
        return null;
    };

    const handleAddEntryClick = () => {
        showPopup(() => (
            <AddEntryPopup
                timezone={_org.timezone}
                onSubmit={handleSubmitNewEntry}
            />
        ), true);
    };

    if (!userId) {
        return <Navigate to="/bancoDeHoras" />;
    }

    return (
        <BareScreen
            title={`Detalle para ${state?.name || "No especificado"}`}
            subtitle={`Organización: ${_org.name}`}
            backgroundColour="#F5F4F7"
            showBackButton={true}
            justifyContent={"center"}
            onBackClick={() => navigate(-1)}
            scrollable={true}
        >
            <div className={styles["detail-container"]}>
                <BalanceHeader entries={entries} loading={loading} />
                <EntriesTable entries={entries} onAddEntryClick={handleAddEntryClick} loading={loading} />
                <div style={{minHeight: '24px'}}/>
            </div>
        </BareScreen>
    );
};

export default HourBankDetailScreen;
